export const VITE_PRELOAD_RECOVERY_KEY = 'lw:vite-preload-recovery-attempted';

export type VitePreloadRecoveryState =
  | 'reloading'
  | 'exhausted'
  | 'storage-unavailable';

interface StorageReader {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

interface StorageRemover {
  removeItem(key: string): void;
}

interface PreloadRecoveryDependencies {
  shouldRecover?: () => boolean;
  getStorage: () => StorageReader;
  reload: () => void;
  markState?: (state: VitePreloadRecoveryState) => void;
}

/**
 * Build the Vite preload-error listener around injectable browser primitives.
 *
 * Vite throws a failed dynamic import unless this cancelable event is
 * prevented. During a deploy-window race that rejection would let React's
 * error boundary replace useful prerendered content. Prevent the rejection,
 * record one recovery attempt in sessionStorage, and perform one document
 * reload so the browser receives the current HTML-to-chunk graph.
 *
 * If sessionStorage is unavailable, fail closed without reloading: a
 * document-local flag cannot survive reload and therefore cannot prevent a
 * loop. The existing SSR content remains the useful fallback.
 */
export function createVitePreloadErrorHandler({
  shouldRecover = () => true,
  getStorage,
  reload,
  markState = () => undefined,
}: PreloadRecoveryDependencies): EventListener {
  return (event) => {
    // Stateful/private SPA surfaces intentionally retain their existing error
    // boundaries and manual recovery. This guard protects public prerendered
    // content from being replaced during a deploy-window hydration race.
    if (!shouldRecover()) return;

    event.preventDefault();

    let storage: StorageReader;
    try {
      storage = getStorage();
      if (storage.getItem(VITE_PRELOAD_RECOVERY_KEY) === 'true') {
        markState('exhausted');
        return;
      }
      storage.setItem(VITE_PRELOAD_RECOVERY_KEY, 'true');
    } catch {
      markState('storage-unavailable');
      return;
    }

    markState('reloading');
    reload();
  };
}

export function clearVitePreloadRecoveryFlag(storage: StorageRemover): void {
  try {
    storage.removeItem(VITE_PRELOAD_RECOVERY_KEY);
  } catch {
    // Storage can be disabled in privacy modes. Recovery already fails closed.
  }
}

// Optional UI chunks fail quietly: they never reload the page or mark this
// document's graph as failed. The consent notice is the only one, loaded
// through importOptional. A failure naming its chunk is optional; so is one
// whose message names no chunk at all (Safari's wording) while an optional
// import is in flight. Failures naming any other chunk still recover.
const OPTIONAL_CHUNK_PATTERN = /\/ConsentNotice-[\w-]+\.js\b/;
const NAMES_A_CHUNK = /\.(?:js|css)\b/;
let optionalImportsPending = 0;

export function isOptionalChunkFailure(event: Event): boolean {
  const payload = (event as Event & { payload?: unknown }).payload;
  if (!(payload instanceof Error)) return false;
  if (OPTIONAL_CHUNK_PATTERN.test(payload.message)) return true;
  return optionalImportsPending > 0 && !NAMES_A_CHUNK.test(payload.message);
}

/** Load optional UI: any failure resolves to undefined and never triggers recovery. */
export async function importOptional<T>(load: () => Promise<T>): Promise<T | undefined> {
  optionalImportsPending += 1;
  try {
    return await load();
  } catch {
    return undefined;
  } finally {
    optionalImportsPending -= 1;
  }
}

let listenerInstalled = false;
// A route can still commit after a failed stylesheet (the import proceeds
// once the error is prevented). That commit does not prove the document and
// its assets agree, so it must not re-arm recovery: doing so reloaded the
// page indefinitely under a persistent stylesheet failure.
let errorSeenThisDocument = false;

/** Install once, before React begins hydrating a lazy public route. */
export function installVitePreloadRecovery(): void {
  if (listenerInstalled || typeof window === 'undefined') return;

  const handler = createVitePreloadErrorHandler({
    shouldRecover: () =>
      document.getElementById('root')?.dataset.lwPrerendered === 'true',
    getStorage: () => window.sessionStorage,
    reload: () => window.location.reload(),
    markState: (state) => {
      document.documentElement.dataset.lwPreloadRecovery = state;
    },
  });

  window.addEventListener('vite:preloadError', (event) => {
    if (isOptionalChunkFailure(event)) return;
    errorSeenThisDocument = true;
    handler(event);
  });
  listenerInstalled = true;

  // Prerendered pages record failed render-blocking stylesheets before any
  // module runs (scripts/build-prerendered-pages.mjs). Vite never tracks
  // those links, so route the failure through the same single recovery.
  const styleFailures = (window as Window & { __lwStyleFailures?: string[] }).__lwStyleFailures;
  if (styleFailures?.length) {
    const event = new Event('vite:preloadError', { cancelable: true }) as Event & { payload?: Error };
    event.payload = new Error(`Unable to load stylesheet ${styleFailures[0]}`);
    window.dispatchEvent(event);
  }
}

/**
 * A committed lazy route proves the current HTML and chunk graph agree. Only
 * then may a future deployment race receive its own single recovery attempt.
 */
export function completeVitePreloadRecovery(): void {
  if (typeof window === 'undefined' || errorSeenThisDocument) return;
  try {
    clearVitePreloadRecoveryFlag(window.sessionStorage);
  } catch {
    // Accessing the storage getter itself can throw in hardened privacy modes.
  }
  delete document.documentElement.dataset.lwPreloadRecovery;
}
